import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import Marquee from '../components/Marquee';
import MoreStories from '../components/MoreStories';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';

const galleryPhotos = [
  {
    src: '/portfolio-fairytale.jpg',
    caption: 'The First Glance',
    location: 'Under the Willow Tree',
  },
  {
    src: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=85',
    caption: 'The Procession',
    location: 'Garden Aisle at Dusk',
  },
  {
    src: '/story-chapter3-arch.jpg',
    caption: 'The Sanctuary',
    location: 'Stone Archway Aisle',
  },
  {
    src: '/story-chapter4-fireworks.jpg',
    caption: 'A Sky Aflame',
    location: 'Fireworks Over the Embrace',
  },
  {
    src: '/story-chapter5-cake.jpg',
    caption: 'The Sweetest Vow',
    location: 'Cake & Roses by Candlelight',
  },
  {
    src: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1600&q=85',
    caption: 'The Final Light',
    location: 'A Walk Into Forever',
  },
];

const FairyTaleStory = () => {
  const [storyOpen, setStoryOpen] = useState(false);
  const storyRef = useRef(null);
  const lightbox = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleViewStory = () => {
    setStoryOpen(true);
    setTimeout(() => {
      storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="Beneath the Willow's Glow — A Fairy Tale Wedding | Dion Magee Events"
        description="A cinematic Hudson Valley wedding under a willow tree of Edison bulbs. Step inside the story of a fairy-tale evening produced by Sir Dion Magee Events & Weddings."
        path="/portfolio/fairy-tale-wedding"
        image="/portfolio-fairytale.jpg"
        type="article"
        article={{
          headline: "Beneath the Willow's Glow — A Fairy Tale Wedding",
          datePublished: '2024-10-12',
          section: 'Wedding Stories',
        }}
      />
      {/* HERO */}
      <section className="relative h-[90vh] w-full overflow-hidden">
        <img
          src="/portfolio-fairytale.jpg"
          alt="Beneath the Willow's Glow"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />

        {/* Back link */}
        <div className="absolute top-8 left-6 lg:top-12 lg:left-12 z-20">
          <Link
            to="/#portfolio"
            data-testid="back-to-portfolio"
            className="inline-flex items-center gap-3 text-white/80 hover:text-[#c9a96e] transition-colors text-[11px] tracking-[0.4em] uppercase"
          >
            <ArrowLeft size={16} />
            Back to Portfolio
          </Link>
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-20 lg:pb-28 px-6 text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">
            A Wedding Story
          </p>
          <h1
            className="font-serif italic text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl leading-[1.05]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Beneath the Willow&rsquo;s Glow
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mt-10" />
          <p className="text-white/70 text-sm tracking-[0.35em] uppercase mt-6">
            Hudson Valley, NY &nbsp;&middot;&nbsp; An Autumn Evening
          </p>
        </div>
      </section>

      {/* TOP GALLERY - 3 photos */}
      <section className="bg-[#0a0a0a] py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-5">The Chapters</p>
            <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl">
              A Day Composed in Light
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-7" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {galleryPhotos.slice(0, 3).map((p, i) => (
              <figure
                key={i}
                data-testid={`fairy-photo-${i}`}
                onClick={() => lightbox.openAt(i)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 1).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* GOLD ELEGANT "VIEW STORY" CTA - MIDDLE OF PAGE */}
      <section className="relative bg-[#0a0a0a] py-16 lg:py-24 px-6">
        {/* decorative gold orbs */}
        <div className="pointer-events-none absolute -top-20 left-1/4 w-64 h-64 rounded-full bg-[#c9a96e]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 right-1/4 w-72 h-72 rounded-full bg-[#c9a96e]/10 blur-3xl" />

        <div className="relative max-w-3xl mx-auto text-center">
          {/* Decorative lines */}
          <div className="flex items-center justify-center gap-6 mb-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>

          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">A Tale Worth Telling</p>
          <h3
            className="font-serif italic text-white text-3xl md:text-4xl lg:text-5xl mb-10 leading-snug"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Step into their evening &mdash; a story written in willow light.
          </h3>

          {/* THE BOX */}
          <button
            type="button"
            onClick={handleViewStory}
            data-testid="view-story-button"
            className="group relative inline-block px-14 sm:px-20 py-6 sm:py-7 border border-[#c9a96e] bg-transparent overflow-hidden transition-colors duration-500 hover:bg-[#c9a96e]/10"
          >
            {/* gold corner accents */}
            <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#e8d5a8]" />
            <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#e8d5a8]" />
            <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#e8d5a8]" />
            <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#e8d5a8]" />

            <span
              className="font-serif italic text-[#e8d5a8] text-2xl sm:text-3xl tracking-[0.2em] relative z-10"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              View Story
            </span>
            <span className="block absolute inset-x-10 bottom-3 h-px bg-[#c9a96e]/0 group-hover:bg-[#c9a96e]/50 transition-all duration-500" />
          </button>

          <div className="flex items-center justify-center gap-6 mt-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>
        </div>
      </section>

      {/* STORY NARRATIVE (revealed on click) */}
      <section
        ref={storyRef}
        className={`bg-[#0a0a0a] px-6 lg:px-12 overflow-hidden transition-all duration-700 ease-out ${
          storyOpen ? 'max-h-[6000px] opacity-100 py-20 lg:py-28' : 'max-h-0 opacity-0 py-0'
        }`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.55em] uppercase mb-6">The Story</p>
            <h2
              className="font-serif italic text-white text-4xl md:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              How They Found Forever
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-8" />
          </div>

          <div
            className="space-y-7 text-white/80 text-lg leading-[1.9] font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="fairy-story-text"
          >
            <p className="first-letter:font-serif first-letter:italic first-letter:text-[#c9a96e] first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]">
              Once, when autumn had laid its softest light upon the Hudson Valley, two souls
              chose a single tree to stand witness to their forever. The old weeping willow had
              watched a hundred summers pass; tonight, it would cradle a love story all its own.
            </p>

            <p>
              They met as friends at the edge of a quiet lake, where evenings smelled of moss
              and lanterns. He brought books. She brought questions. Together they spoke until
              the stars grew tired &mdash; and somewhere between the small confessions and the long
              silences, the world rearranged itself around them.
            </p>

            <p>
              On the evening of the wedding, the willow was hung with hand-blown Edison bulbs,
              each one a little captured sun. Guests followed a corridor of candles to the
              water&rsquo;s edge, where the bride appeared in dusk-pale silk, a bouquet of garden
              roses gathered close to her heart.
            </p>

            <p>
              He waited beneath the branches, the cuffs of his jacket dark against the gold
              light. When she reached him, the breeze stilled. He whispered, &ldquo;You came.&rdquo;
              And she answered, smiling, &ldquo;I was always going to.&rdquo;
            </p>

            <p>
              They were married as the lanterns came alive across the lake. They danced beneath
              the willow long after the last toast. They walked home together &mdash; barefoot, hand
              in hand, glowing faintly against the dark &mdash; into a life that would always begin
              with this single, luminous night.
            </p>

            <p className="text-center text-[#c9a96e] italic pt-6">
              &mdash; And as in every true fairy tale, they lived in love, ever after.
            </p>
          </div>

          {/* BOTTOM GALLERY - 3 more photos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mt-20">
            {galleryPhotos.slice(3).map((p, i) => (
              <figure
                key={i + 3}
                data-testid={`fairy-photo-${i + 3}`}
                onClick={() => lightbox.openAt(i + 3)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 4).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Closing */}
          <div className="text-center mt-20">
            <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-8" />
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">An Original Production by</p>
            <p
              className="font-serif italic text-white text-3xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Dion Magee Events &amp; Weddings
            </p>
            <Link
              to="/#contact"
              data-testid="story-cta-enquire"
              className="inline-block mt-10 px-10 py-4 border border-[#c9a96e] text-[#e8d5a8] text-[11px] tracking-[0.4em] uppercase hover:bg-[#c9a96e] hover:text-black transition-colors duration-500"
            >
              Begin Your Own Story
            </Link>
          </div>
        </div>
      </section>

      <MoreStories currentSlug="fairy-tale-wedding" />
      <Marquee />
      <Footer />
      <Lightbox
        open={lightbox.open}
        onClose={lightbox.close}
        photos={galleryPhotos}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
      />
    </div>
  );
};

export default FairyTaleStory;
